import { useCallback } from 'react'

import { useLocalStorage } from 'hooks/use-local-storage'
import { useUser } from 'hooks/use-user'
import { Mixpanel } from 'utils/mixpanel'

import { ONBOARDING_STEPS } from '../config/steps'

import { useShouldShowOnboarding } from './use-should-show-onboarding'

export const STORAGE_KEY_PREFIX = 'onboarding_state'

export interface OnboardingStateReturn {
  state: OnboardingState
  updateFormData: (data: Partial<OnboardingFormData>) => void
  completeOnboarding: () => void
  openModal: (source?: 'direct' | 'notification') => void
  closeModal: () => void
  onboardingQuestionnaireEnabled: boolean
  nextStep: () => void
  previousStep: () => void
}

export interface OnboardingFormData {
  step2?: {
    roleId: number | null | undefined
    role?: string
    jobTitle?: string
  }
  step3?: {
    comfortLevel?: string
  }
  step4?: {
    motivation?: string
    specifiedMotivation?: string
  }
  step5?: {
    industry?: string
    specifiedIndustry?: string
  }
  step6?: {
    companySize?: string
  }
}

export interface OnboardingState {
  currentStep: number
  completedSteps: string[]
  isOnboardingModalOpen: boolean
  isCompleted: boolean
  lastInteractionDate: string | null
  formData: OnboardingFormData
  completionSource?: 'direct' | 'notification'
}

const INITIAL_STATE: OnboardingState = {
  currentStep: 1,
  completedSteps: [],
  isOnboardingModalOpen: false,
  isCompleted: false,
  lastInteractionDate: null,
  formData: {},
  completionSource: 'direct',
}

/**
 * Hook to manage onboarding state and interactions
 * Uses experiment state to determine visibility
 * @returns Object containing onboarding state and management functions
 */
export function useOnboarding(): OnboardingStateReturn {
  const user = useUser()
  const { isEnabled } = useShouldShowOnboarding()
  const storageKey = `${STORAGE_KEY_PREFIX}_${user?.id || 'anonymous'}`

  const [state, setStateBase] = useLocalStorage<OnboardingState>(
    storageKey,
    INITIAL_STATE,
  )

  const setState = useCallback(
    (
      updater: ((prev: OnboardingState) => OnboardingState) | OnboardingState,
    ) => {
      try {
        setStateBase(updater)
      } catch (error) {
        console.error('Failed to update onboarding state:', error)
        setTimeout(() => {
          try {
            setStateBase(updater)
          } catch (retryError) {
            console.error(
              'Failed to retry onboarding state update:',
              retryError,
            )
          }
        }, 100)
      }
    },
    [setStateBase],
  )

  const openModal = useCallback(
    (source: 'direct' | 'notification' = 'direct') => {
      if (!isEnabled) return

      setState((prev) => {
        Mixpanel.trackOnboardingQuestionnaireOpen({
          source,
        })

        Mixpanel.startRecording()

        return {
          ...prev,
          isOnboardingModalOpen: true,
          lastInteractionDate: new Date().toISOString(),
          completionSource: source,
        }
      })
    },
    [setState, isEnabled],
  )

  const closeModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOnboardingModalOpen: false,
      lastInteractionDate: new Date().toISOString(),
    }))

    Mixpanel.stopRecording()
  }, [setState])

  const updateFormData = useCallback(
    (data: Partial<OnboardingFormData>) => {
      if (!data || typeof data !== 'object') {
        console.error('Invalid form data provided:', data)
        return
      }

      setState((prev) => ({
        ...prev,
        formData: { ...prev.formData, ...data },
        lastInteractionDate: new Date().toISOString(),
        completionSource: prev.completionSource || 'direct',
      }))
    },
    [setState],
  )

  const completeOnboarding = useCallback(() => {
    setState((prev) => {
      Mixpanel.trackOnboardingQuestionnaireComplete({
        completedLocation: prev.completionSource || 'direct',
      })

      Mixpanel.stopRecording()

      return {
        ...prev,
        isCompleted: true,
        lastInteractionDate: new Date().toISOString(),
      }
    })
  }, [setState])

  const nextStep = useCallback(() => {
    setState((prev) => {
      const currentState = JSON.parse(
        window.localStorage.getItem(storageKey) || JSON.stringify(prev),
      )

      const newState = {
        ...currentState,
        currentStep: Math.min(
          currentState.currentStep + 1,
          Object.keys(ONBOARDING_STEPS).length,
        ),
        lastInteractionDate: new Date().toISOString(),
        formData: { ...currentState.formData },
        completionSource: currentState.completionSource || 'direct',
      }

      return newState
    })
  }, [setState, storageKey])

  const previousStep = useCallback(() => {
    setState((prev) => {
      const currentState = JSON.parse(
        window.localStorage.getItem(storageKey) || JSON.stringify(prev),
      )

      const newState = {
        ...currentState,
        currentStep: Math.max(prev.currentStep - 1, 1),
        lastInteractionDate: new Date().toISOString(),
        formData: { ...currentState.formData },
        completionSource: currentState.completionSource || 'direct',
      }

      return newState
    })
  }, [setState, storageKey])

  const onboardingQuestionnaireEnabled = isEnabled && !state.isCompleted

  return {
    state,
    updateFormData,
    completeOnboarding,
    openModal,
    closeModal,
    onboardingQuestionnaireEnabled,
    nextStep,
    previousStep,
  }
}
